import { Link, useLocation } from "react-router-dom";
import { SubmitHandler, useForm } from "react-hook-form";
import { LockIcon } from "lucide-react";

import { useAppNavigate } from "@utils/hooks/useAppNavigate";
import { getQueryFromUrl } from "@utils/helpers/getQueryFromUrl";
import { resetPassword, useResetPassword } from "@lib/stores";
import FormTextInput from "@components/items/inputs/FormTextInput";
import { Server } from "@modules/api";
import TranslatedText from "@components/items/display/TranslatedText";
import { ShadcnButton } from "@components/items/display/ShadcnButton";
import { translate } from "@modules/localizations/translate";
import { Card } from "@ui/card";

type Inputs = {
  password: string;
};

export const ResetPassword = () => {
  const navigate = useAppNavigate();
  const location = useLocation();

  const emailSearchParam = getQueryFromUrl("email");
  const createSearchParam = getQueryFromUrl("create");
  const codeSearchParam = getQueryFromUrl("code");

  const { status } = useResetPassword();

  const { handleSubmit, control, formState } = useForm<Inputs>({
    mode: "all",
    defaultValues: { password: "" },
  });

  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    resetPassword(
      {
        password: data.password,
        email: emailSearchParam || "",
        code: codeSearchParam || "",
      },
      () => {
        if (location.pathname.includes("b2b")) {
          navigate(`/login/b2b`);
        } else {
          navigate("/login");
        }
      }
    );
  };

  const isDisabled =
    !formState.isDirty ||
    Object.keys(formState.errors).length !== 0 ||
    status === "loading";

  return (
    <Card className="relative w-[434px] border-none bg-dark_background p-5">
      <div className="flex w-full flex-col items-center justify-between space-y-6 text-white sm:justify-center">
        <div className="flex w-full flex-col items-center justify-center space-y-6">
          <div className="px-9 text-center text-[22px] font-medium">
            {createSearchParam === "1" ? (
              <TranslatedText text="onboarding.titles.create password" />
            ) : (
              <TranslatedText text="onboarding.titles.reset password" />
            )}
          </div>
          <form
            className="mx-auto flex w-full flex-col space-y-2 px-4 sm:w-[375px] sm:px-0"
            onSubmit={handleSubmit(onSubmit)}
          >
            <FormTextInput
              inputHtmlType="password"
              inputExtraClassNames="placeholder:rtl:pr-1 placeholder:ltr:pl-1 !bg-[#D9D9D9]"
              prefix={<LockIcon className="h-5 w-5 text-primary" />}
              fieldMeta={{
                fieldType: Server.FieldType.Text,
                placeHolder: translate(
                  "onboarding.inputs.placeholders.new password"
                ),
                required: true,
                fieldOptions: {
                  minLength: 8,
                },
              }}
              control={control}
              name="password"
              validationErrorMessages={{
                minLength: "The password must be at least 8 characters",
              }}
              onEnter={() => {
                handleSubmit(onSubmit)();
              }}
              required
            />
            <br />
            <div className="flex w-full items-center justify-center gap-5">
              <ShadcnButton
                className="bg-transparent hover:bg-transparent/20"
                onClick={() => {
                  navigate(-1);
                }}
                type="button"
              >
                <TranslatedText
                  text={`onboarding.buttons.${"cancel"}`}
                  className="text-[16px] font-normal leading-[22px] text-white"
                />
              </ShadcnButton>
              <ShadcnButton
                disabled={isDisabled}
                className="px-6 text-white"
                onClick={handleSubmit(onSubmit)}
                isLoading={status === "loading"}
              >
                <TranslatedText
                  text={`onboarding.buttons.reset password`}
                  className="text-[16px] font-normal leading-[22px]"
                />
              </ShadcnButton>
            </div>
          </form>
        </div>
        {/* <div className="pt-8 pb-4 w-full">
          <div className="font-medium text-center flex justify-center items-center gap-1">
            <span className="text-white">
              <TranslatedText text="onboarding.titles.have you faced problems?" />
            </span>
            <span className="text-primary dark:text-primary-dark">
              <Link to="/ar/contact-us">
                <TranslatedText text="onboarding.titles.contact us" />
              </Link>
            </span>
          </div>
        </div> */}
      </div>
    </Card>
  );
};
